import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from '@vaiu/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@vaiu/ui/tabs';
import { Badge } from '@vaiu/ui/badge';
import { Button } from '@vaiu/ui/button';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Progress } from '@vaiu/ui/progress';
import { Separator } from '@vaiu/ui/separator';
import {
  Clock,
  Package,
  User,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Bot,
  Loader,
  ArrowUpRight,
  ArrowDownLeft,
  Shield,
  FileCheck,
  Truck,
  CreditCard,
  UserCheck,
  Database
} from 'lucide-react';
import { platformApi } from '@/services/platformApi';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

interface SmartTransaction {
  id: string;
  type: 'marketplace_purchase' | 'service_payment' | 'conditional_payment' | 'identity_verification' | 'data_verification';
  direction: 'incoming' | 'outgoing';
  counterparty: {
    id: string;
    name: string;
    avatar?: string;
  };
  amount: number;
  currency: string;
  status: 'pending_verification' | 'verified' | 'disputed' | 'auto_verifying';
  verificationMethod: 'manual_self' | 'manual_counterparty' | 'automated_delivery' | 'automated_identity' | 'automated_data' | 'smart_contract';
  verificationService?: string;
  verificationStatus?: string;
  requiredAction?: string;
  autoReleaseDate?: string;
  createdAt: string;
  metadata: Record<string, any>;
}

interface SmartTransactionInboxProps {
  userRole?: 'buyer' | 'seller' | 'both';
}

export function SmartTransactionInbox({ userRole = 'both' }: SmartTransactionInboxProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<'incoming' | 'outgoing'>(
    userRole === 'seller' ? 'incoming' : 'outgoing'
  );
  const [transactions, setTransactions] = useState<{
    incoming: SmartTransaction[];
    outgoing: SmartTransaction[];
  }>({
    incoming: [],
    outgoing: []
  });
  const [summary, setSummary] = useState({
    incomingTotal: 0,
    outgoingTotal: 0,
    requiresAction: 0,
    automatedPending: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSmartTransactions();
    // Poll for updates every 30 seconds
    const interval = setInterval(loadSmartTransactions, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadSmartTransactions = async () => {
    try {
      const response = await platformApi.get('/v2/escrow/smart-transactions', {
        params: { role: userRole }
      });
      setTransactions({
        incoming: response.data.incoming || [],
        outgoing: response.data.outgoing || []
      });
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Failed to load smart transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (transaction: SmartTransaction, action: 'approve' | 'reject') => {
    try {
      await platformApi.post('/v2/escrow/verify', {
        escrowId: transaction.id,
        action,
        verificationType: 'manual'
      });
      
      toast({
        title: action === 'approve' ? 'Funds Released!' : 'Transaction Disputed',
        description: action === 'approve' 
          ? `${transaction.amount} ${transaction.currency} has been released`
          : 'Support team will review this transaction',
        variant: action === 'approve' ? 'default' : 'destructive'
      });
      
      loadSmartTransactions();
    } catch (error) {
      toast({
        title: 'Verification Failed',
        description: 'Unable to process verification. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const getVerificationIcon = (method: string) => {
    switch (method) {
      case 'manual_self':
      case 'manual_counterparty':
        return <User className="w-5 h-5 text-orange-500" />;
      case 'automated_delivery':
        return <Truck className="w-5 h-5 text-blue-500" />;
      case 'automated_identity':
        return <UserCheck className="w-5 h-5 text-green-500" />;
      case 'automated_data':
        return <Database className="w-5 h-5 text-purple-500" />;
      case 'smart_contract':
        return <FileCheck className="w-5 h-5 text-indigo-500" />;
      default:
        return <Shield className="w-5 h-5 text-gray-500" />;
    }
  };

  const TransactionCard = ({ transaction }: { transaction: SmartTransaction }) => {
    const isAutomated = transaction.verificationMethod.startsWith('automated_');
    const requiresMyAction = transaction.verificationMethod === 'manual_self';
    
    return (
      <Card className="mb-4">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="mt-1">
                {transaction.direction === 'incoming' ? (
                  <ArrowDownLeft className="w-6 h-6 text-green-500" />
                ) : (
                  <ArrowUpRight className="w-6 h-6 text-blue-500" />
                )}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <h4 className="font-semibold">
                    {transaction.direction === 'incoming' ? 'From' : 'To'}: {transaction.counterparty.name}
                  </h4>
                  {getVerificationIcon(transaction.verificationMethod)}
                </div>
                
                <p className="text-2xl font-bold mb-2">
                  {transaction.currency} {transaction.amount.toFixed(2)}
                </p>
                
                <div className="space-y-2">
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <span className="flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatDistanceToNow(new Date(transaction.createdAt), { addSuffix: true })}
                    </span>
                    {transaction.autoReleaseDate && (
                      <span className="flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Auto-release in {formatDistanceToNow(new Date(transaction.autoReleaseDate))}
                      </span>
                    )}
                  </div>
                  
                  {/* Verification Status */}
                  <div className="flex items-center space-x-2">
                    {isAutomated ? (
                      <>
                        <Badge variant="secondary" className="flex items-center space-x-1">
                          <Bot className="w-3 h-3" />
                          <span>Automated Verification</span>
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          via {transaction.verificationService}
                        </span>
                        {transaction.status === 'auto_verifying' && (
                          <Loader className="w-3 h-3 animate-spin" />
                        )}
                      </>
                    ) : (
                      <Badge 
                        variant={requiresMyAction ? 'destructive' : 'outline'}
                        className="flex items-center space-x-1"
                      >
                        {requiresMyAction ? (
                          <>
                            <AlertCircle className="w-3 h-3" />
                            <span>Action Required</span>
                          </>
                        ) : (
                          <>
                            <Clock className="w-3 h-3" />
                            <span>Awaiting {transaction.direction === 'incoming' ? 'Your' : "Buyer's"} Confirmation</span>
                          </>
                        )}
                      </Badge>
                    )}
                  </div>
                  
                  {transaction.requiredAction && (
                    <Alert className="mt-2">
                      <AlertDescription>
                        {transaction.requiredAction}
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {/* Metadata Display */}
                  {transaction.metadata.listingTitle && (
                    <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                      <span className="font-medium">Item:</span> {transaction.metadata.listingTitle}
                    </div>
                  )}
                  
                  {transaction.metadata.trackingNumber && (
                    <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                      <span className="font-medium">Tracking:</span> {transaction.metadata.trackingNumber}
                      <a href={transaction.metadata.trackingUrl} className="ml-2 text-blue-600 hover:underline">
                        Track Package →
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col space-y-2">
              {requiresMyAction && (
                <>
                  <Button
                    size="sm"
                    onClick={() => handleVerification(transaction, 'approve')}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Release Funds
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleVerification(transaction, 'reject')}
                  >
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Dispute
                  </Button>
                </>
              )}
              
              {!requiresMyAction && !isAutomated && transaction.direction === 'incoming' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {/* Open messaging */}}
                >
                  Contact {transaction.direction === 'incoming' ? 'Buyer' : 'Seller'}
                </Button>
              )}
              
              {isAutomated && transaction.status === 'auto_verifying' && (
                <div className="text-center">
                  <Loader className="w-5 h-5 animate-spin mx-auto mb-1" />
                  <span className="text-xs text-muted-foreground">Verifying...</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Loading smart transactions...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Incoming Escrow</p>
                <p className="text-2xl font-bold">${summary.incomingTotal.toFixed(2)}</p>
              </div>
              <ArrowDownLeft className="w-8 h-8 text-green-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Outgoing Escrow</p>
                <p className="text-2xl font-bold">${summary.outgoingTotal.toFixed(2)}</p>
              </div>
              <ArrowUpRight className="w-8 h-8 text-blue-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Requires Action</p>
                <p className="text-2xl font-bold">{summary.requiresAction}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-orange-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Auto-Verifying</p>
                <p className="text-2xl font-bold">{summary.automatedPending}</p>
              </div>
              <Bot className="w-8 h-8 text-purple-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transaction Tabs */}
      <Card>
        <CardHeader>
          <CardTitle>Smart Transactions</CardTitle>
          <CardDescription>
            Transactions requiring verification before completion
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'incoming' | 'outgoing')}>
            <TabsList className="grid w-full grid-cols-2">
              {(userRole === 'seller' || userRole === 'both') && (
                <TabsTrigger value="incoming" className="flex items-center space-x-2">
                  <ArrowDownLeft className="w-4 h-4" />
                  <span>Incoming</span>
                  {transactions.incoming.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {transactions.incoming.length}
                    </Badge>
                  )}
                </TabsTrigger>
              )}
              {(userRole === 'buyer' || userRole === 'both') && (
                <TabsTrigger value="outgoing" className="flex items-center space-x-2">
                  <ArrowUpRight className="w-4 h-4" />
                  <span>Outgoing</span>
                  {transactions.outgoing.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {transactions.outgoing.length}
                    </Badge>
                  )}
                </TabsTrigger>
              )}
            </TabsList>
            
            <TabsContent value="incoming" className="mt-6">
              {transactions.incoming.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="w-12 h-12 mx-auto mb-4 opacity-20" />
                  <p>No incoming escrowed payments</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {transactions.incoming.map(tx => (
                    <TransactionCard key={tx.id} transaction={tx} />
                  ))}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="outgoing" className="mt-6">
              {transactions.outgoing.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-20" />
                  <p>No outgoing escrowed payments</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {transactions.outgoing.map(tx => (
                    <TransactionCard key={tx.id} transaction={tx} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
